<template>
  <div class="position-relative events-subtable">
    <grid-table
      :columns="columns"
      :rows="totalsRows"
      :groups="groups"
      :loading="loadingEvents || reordering"
      :open-id="activeEventId"
      :draggable="true"
      empty-text="No events found for this draft."
      :mob-break="868"
      class="gt--nested"
      @drag-end="onDragEnd"
    >
      <template #before-row="{ row }">
        <row-alert
          v-if="alert && alert.message && anchorRowId === row.id"
          :message="alert.message"
          :type="alert.type"
          :alert-key="alert.id"
          @dismiss="$emit('dismiss-alert')"
        />
      </template>

      <template #cell:drag="{ row }">
        <app-tooltip v-if="dragKind(row)" :text="dragTitle(row)">
          <div
            class="icon-btn icon-btn--drag gt__drag-handle"
            :class="[dragKind(row) === 'group' ? 'gt__drag-handle--group' : 'gt__drag-handle--child', { 'is-disabled': reordering }]"
          />
        </app-tooltip>
      </template>

      <template #cell:name="{ row }">
        <div v-if="row.id === 'totals'">
          <strong>Total</strong>
          <small v-if="row.unique_accepted_competitors != null" class="d-block text-muted">
            Unique accepted<br>
            <span class="text-nowrap">competitors: <strong>{{ row.unique_accepted_competitors }}</strong></span>
          </small>
        </div>
        <span
          v-else-if="row.is_parent"
          class="text-primary font-weight-bold"
        >{{ row.name }} (original)</span>
        <router-link
          v-else
          class="btn btn-link p-0 text-left event-name-btn"
          :class="{ 'event-name-btn--active': activeEventId === row.id, 'event-name-btn--child': row.parent_id != null }"
          :to="eventLink(row.id)"
        >
          {{ row.name }}
        </router-link>
      </template>

      <template #cell:final_noms="{ row }">
        {{ row.final_noms || '' }}
      </template>

      <template #cell:total_noms="{ row }">
        {{ row.total_noms }}<span v-if="row.total_noms_quantity !== row.total_noms" class="text-primary"> ({{ row.total_noms_quantity }})</span>
      </template>

      <template #cell:unprocessed="{ row }">
        {{ row.unprocessed }}<span v-if="row.unprocessed_quantity !== row.unprocessed" class="text-primary"> ({{ row.unprocessed_quantity }})</span>
      </template>

      <template #cell:accepted="{ row }">
        {{ row.accepted }}<span v-if="row.accepted_first_round && row.parent_id !== null" class="text-primary"> ({{ row.accepted_first_round }})</span><span v-else-if="row.accepted_quantity !== row.accepted" class="text-primary"> ({{ row.accepted_quantity }})</span>
      </template>

      <template #cell:wait_list="{ row }">
        {{ row.wait_list }}<span v-if="row.wait_list_quantity !== row.wait_list" class="text-primary"> ({{ row.wait_list_quantity }})</span>
      </template>

      <template #cell:scratched="{ row }">
        {{ row.scratched }}<span v-if="row.scratched_quantity !== row.scratched" class="text-primary"> ({{ row.scratched_quantity }})</span>
      </template>

      <template #cell:actions="{ row }">
        <div v-if="row.id !== 'totals'" class="actions__wrap">
          <app-tooltip v-if="row.parent_id === null" :text="splitTitle(row)">
            <button
              class="btn-actions icon-btn icon-btn--split"
              :class="{ 'is-split': row.brother_id !== null || !canSplit(row) }"
              @click="canSplit(row) && $emit('confirm-split', row)"
            />
          </app-tooltip>
          <app-tooltip v-if="!row.is_parent" :text="row.is_locked ? 'Unlock Event Nominations' : 'Lock Event Nominations'">
            <button
              class="btn-actions icon-btn"
              :class="row.is_locked ? 'icon-btn--lock' : 'icon-btn--unlock'"
              @click="$emit('lock', row.id, !row.is_locked)"
            />
          </app-tooltip>
          <app-tooltip text="Edit Event Details, Change Event Restrictions" align="right">
            <router-link
              :to="{ name: 'Event Edit', params: { id: draftId, eventId: row.parent_id != null ? row.parent_id : row.id } }"
              class="icon-btn icon-btn--edit"
            />
          </app-tooltip>
          <app-tooltip v-if="row.parent_id === null && row.split_kind !== EVENT_SPLIT_KIND_COMBINED_CONJUNCTION_FINAL" text="Delete Event" align="right">
            <button
              class="btn-actions btn-delete"
              :disabled="deleting"
              @click="$emit('confirm-delete', row)"
            />
          </app-tooltip>
        </div>
      </template>
    </grid-table>
  </div>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'
import GridTable from '@/components/GridTable'
import AppTooltip from '@/components/AppTooltip'
import RowAlert from '@/components/RowAlert'
import { EVENT_SPLIT_KIND_COMBINED_CONJUNCTION_FINAL } from '@/constants/eventSplitKind'
import { buildEventGroups, flattenEventGroups } from '@/utils/eventGroups'

const COLUMNS = [
  { key: 'drag', label: '', width: '50px', mobileWidth: '32px', cellClass: 'valign-center text-center' },
  { key: 'name', label: 'Event Name', width: '1fr', primary: true },
  { key: 'final_noms', label: 'Final Noms', width: '1fr', mobile: 'label', cellClass: 'text-center' },
  { key: 'total_noms', label: 'Total Noms', width: '1fr', mobile: 'label', cellClass: 'text-center' },
  { key: 'unprocessed', label: 'Unprocessed', width: '1fr', mobile: 'label', cellClass: 'text-center' },
  { key: 'accepted', label: 'Accepted', width: '1fr', mobile: 'label', cellClass: 'text-center' },
  { key: 'wait_list', label: 'Waitlist', width: '1fr', mobile: 'label', cellClass: 'text-center' },
  { key: 'scratched', label: 'Scratched', width: '1fr', mobile: 'label', cellClass: 'text-center' },
  { key: 'actions', label: 'Actions', width: '1fr', mobileWidth: '76px', cellClass: 'valign-center' }
]

export default defineComponent({
  name: 'CommitteeDraftEventsTable',
  components: { GridTable, AppTooltip, RowAlert },
  props: {
    events: { type: Array, default: () => [] },
    totals: { type: Object, default: null },
    loadingEvents: { type: Boolean, default: false },
    reordering: { type: Boolean, default: false },
    deleting: { type: Boolean, default: false },
    draftId: { type: Number, required: true },
    draftStateId: { type: Number, default: null },
    activeEventId: { type: Number, default: null },
    list: { type: String, default: null },
    // { message, type, eventId } — the result of an action, shown above the event it was done on
    alert: { type: Object, default: null }
  },
  emits: ['lock', 'drag-end', 'confirm-delete', 'confirm-split', 'dismiss-alert'],
  setup(props, { emit }) {
    const DRAFT_STATE_FINALISE = 5

    function canSplit(row) {
      return row.brother_id === null
        && row.rounds === 2
        && props.draftStateId === DRAFT_STATE_FINALISE
    }

    function splitTitle(row) {
      if (row.brother_id !== null) return 'Event already split'
      if (row.rounds !== 2) return 'Only 2-round events can be split'
      if (props.draftStateId !== DRAFT_STATE_FINALISE) return 'Split is only available when draft is in Finalise state'
      return 'Split event into A and B'
    }

    function eventLink(eventId) {
      const query = {}
      if (props.list) query.list = props.list
      query.event_id = String(eventId)
      return { name: 'Committees Dashboard', params: { draftId: String(props.draftId) }, query }
    }

    const groups = computed(() => buildEventGroups(props.events))

    // Only top-level rows can carry the notice (rows inside a unit live in a nested sortable),
    // so a child event's notice goes above its parent row.
    const anchorRowId = computed(() => {
      if (!props.alert || props.alert.eventId == null) return null
      const event = props.events.find(e => e.id === props.alert.eventId)
      return event && event.parent_id != null ? event.parent_id : props.alert.eventId
    })

    // The totals row sits below the groups and is never draggable.
    const totalsRows = computed(() => (props.totals ? [{ id: 'totals', ...props.totals }] : []))

    // Every top-level row (the original, its B half, a conjunction final) drags the whole group;
    // children only reorder inside their own parent.
    const groupSizes = computed(() => {
      const sizes = new Map()
      groups.value.forEach((group) => {
        const size = flattenEventGroups([group]).length
        group.units.forEach(unit => sizes.set(unit.row.id, size))
      })
      return sizes
    })

    function dragKind(row) {
      if (row.id === 'totals') return null
      return row.parent_id != null ? 'child' : 'group'
    }

    function dragTitle(row) {
      if (props.reordering) return 'Saving event order'
      if (dragKind(row) === 'child') return 'Drag to reorder within the group'
      return groupSizes.value.get(row.id) > 1 ? 'Drag to move the whole group' : 'Drag to reorder'
    }

    function onDragEnd(orderedIds, movedId) {
      emit('drag-end', orderedIds, movedId)
    }

    return { columns: COLUMNS, anchorRowId, eventLink, canSplit, splitTitle, groups, totalsRows, dragKind, dragTitle, onDragEnd, EVENT_SPLIT_KIND_COMBINED_CONJUNCTION_FINAL }
  }
})
</script>

<style scoped>
.events-subtable {
  padding: 20px 0;
}

.event-name-btn {
  text-decoration: none;
}

.event-name-btn--child {
  padding-left: 22px !important;
  position: relative;
}

.event-name-btn--child::before {
  content: '\21B3';
  position: absolute;
  left: 4px;
  color: var(--gray-500);
}

.events-subtable >>> .gt__drag-handle--child {
  opacity: 0.45;
  transform: scale(0.8);
}

.event-name-btn--active {
  text-decoration: underline;
  font-weight: bold;
}

.events-subtable >>> .actions__wrap {
  flex-wrap: wrap;
  row-gap: 4px;
}

.events-subtable >>> .gt__hdr,
.events-subtable >>> .gt__cell {
  overflow-wrap: break-word;
  white-space: normal;
}

.gt__drag-handle.is-disabled,
.gt__drag-handle.is-disabled:hover {
  cursor: default;
  opacity: 0.35;
}
</style>



// WEBPACK FOOTER //
// src/views/Committees/Dashboard/components/CommitteeDraftEventsTable.vue